import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import ModalWrapper from "../../../../../../Components/CustomModal/ModalWrapper";
import { Dropdown } from "primereact/dropdown";
import { getViewIssItems } from "../../../../../../Store/prosafe/proTransaction/ProIssue/getViewIssItems";
import axios from "axios";
import { V_URL } from "../../../../../../BaseUrl";
import moment from "moment";
import { getProStock } from "../../../../../../Store/prosafe/proStock/getProStock";
import { getProject } from "../../../../../../Store/Store/Project/Project";

const ManualIssueModel = ({
  modal,
  itemsArr,
  editingIndex,
  setEditingIndex,
  onSubmit,
  data,
  editItemId,
  setEditItemId,
}) => {
  const [filter, setFilter] = useState({
    date: {
      start: null,
      end: null,
    },
  });

  const [errors, setErrors] = useState({});
  const [totalQty, setTotalQty] = useState(0);
  const dispatch = useDispatch();
  const [stockData, setStockData] = useState({});
  const [receivingRates, setReceivingRates] = useState([]);
  const [loadingReceivingRates, setLoadingReceivingRates] = useState(false);
  const [showRateHelp, setShowRateHelp] = useState(true);

  const itemData = useSelector((state) => state.getItem?.user?.data) || [];
  const projectData = useSelector((state) => state.getProject?.user?.data) || [];

  useEffect(() => {
    dispatch(getProject());
  }, []);

  const initialFormState = {
    item_id: "",
    item_no: "",
    item_length: "",
    item_width: "",
    item_weight: "",
    item_radius: "",
    item_thickness: "",
    unit: "",
    category: "",
    assembly_weight: "",
    assembly_surface_area: "",
    rate: '',
    avg_rate: '',
    amount: '',
    gst: '',
    gst_amount: '',
    total_amount: '',
    heat_no: "",
    plat_no: "",
    tc_no: "",
    remarks: "",
    quantity: "",
    req_qty: "",
    isAuto: false,
    is_returnable: false,
    from_id: null,
    detail_id: null,
    project_id: null,
  };

  const [disable, setDisable] = useState(false);
  const [formData, setFormData] = useState(initialFormState);

  const avgRate = useMemo(() => {
    if (!receivingRates || receivingRates.length === 0) return "0.00";

    let totalValue = 0;
    let totalQty = 0;
    let sumRates = 0;
    let validRateCount = 0;

    receivingRates.forEach((row) => {
      const rate = parseFloat(row.rate) || 0;
      const qty = parseFloat(row.in_qty ?? row.quantity ?? row.qty) || 0;

      if (rate > 0) {
        sumRates += rate;
        validRateCount += 1;
      }

      if (qty > 0 && rate > 0) {
        totalValue += qty * rate;
        totalQty += qty;
      }
    });

    if (totalQty > 0 && totalValue > 0) {
      return (totalValue / totalQty).toFixed(2);
    } else if (validRateCount > 0) {
      return (sumRates / validRateCount).toFixed(2);
    }

    return "0.00";
  }, [receivingRates]);

  const fetchStockData = async () => {
    try {
      const res = await axios.post(`${V_URL}/prosafe/find-issue-item-stock`, {
        item_id: formData.item_id,
      }, {
        headers: {
          Authorization: "Barrer " + localStorage.getItem("PAY_USER_TOKEN"),
        },
      });
      setStockData(res?.data?.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (formData.item_id) {
      fetchStockData();
      fetchReceivingRates();
    } else {
      setReceivingRates([]);
    }
  }, [formData.item_id]);

  const fetchReceivingRates = async () => {
    if (!formData.item_id) return;
    setLoadingReceivingRates(true);
    try {
      const res = await axios.post(
        `${V_URL}/prosafe/get-item-receiving-rates`,
        {
          item_id: formData.item_id,
          firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
          year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
          },
        }
      );
      if (res?.data?.success) {
        setReceivingRates(res?.data?.data || []);
      } else {
        setReceivingRates([]);
      }
    } catch (err) {
      console.log(err);
      setReceivingRates([]);
    } finally {
      setLoadingReceivingRates(false);
    }
  };

  const handleApplyReceivingRate = (rateRow) => {
    const selectedRate = rateRow.rate || 0;
    const selectedGst = rateRow.gst !== undefined && rateRow.gst !== null ? rateRow.gst : formData.gst;

    const qty = parseFloat(formData.quantity) || parseFloat(formData.req_qty) || 0;
    const r = parseFloat(selectedRate) || 0;
    const g = parseFloat(selectedGst) || 0;

    let amtStr = "";
    let gAmtStr = "";
    let totStr = "";

    if (qty > 0 && r > 0) {
      const amt = qty * r;
      const gAmt = (amt * g) / 100;
      const tot = amt + gAmt;
      amtStr = amt.toFixed(2);
      gAmtStr = gAmt.toFixed(2);
      totStr = tot.toFixed(2);
    }

    setFormData((prev) => ({
      ...prev,
      rate: selectedRate,
      gst: selectedGst !== undefined ? selectedGst : prev.gst,
      amount: amtStr,
      gst_amount: gAmtStr,
      total_amount: totStr,
      heat_no: rateRow.heat_no || prev.heat_no,
      plat_no: rateRow.plat_no || prev.plat_no,
      tc_no: rateRow.tc_no || prev.tc_no,
    }));

    toast.success(
      `Applied Rate ₹${Number(selectedRate).toFixed(2)} from Receiving Date ${moment(rateRow.trans_date).format("YYYY-MM-DD")}`
    );
  };

  const handleApplyAvgRate = () => {
    const rateVal = parseFloat(avgRate) || 0;
    if (rateVal <= 0) {
      toast.error("No valid average rate to apply");
      return;
    }
    const qty = parseFloat(formData.quantity) || parseFloat(formData.req_qty) || 0;
    const g = parseFloat(formData.gst) || 0;

    let amtStr = "";
    let gAmtStr = "";
    let totStr = "";

    if (qty > 0 && rateVal > 0) {
      const amt = qty * rateVal;
      const gAmt = (amt * g) / 100;
      const tot = amt + gAmt;
      amtStr = amt.toFixed(2);
      gAmtStr = gAmt.toFixed(2);
      totStr = tot.toFixed(2);
    }

    setFormData((prev) => ({
      ...prev,
      rate: String(rateVal),
      amount: amtStr,
      gst_amount: gAmtStr,
      total_amount: totStr,
    }));

    toast.success(`Applied Average Rate ₹${Number(rateVal).toFixed(2)}`);
  };

  useEffect(() => {
    if (editingIndex !== null && itemsArr[editingIndex]) {
      setFormData(itemsArr[editingIndex]);
    } else {
      setFormData(initialFormState);
    }
  }, [editingIndex, itemsArr]);

  useEffect(() => {
    if (formData.item_id) {
      const selectedItem = itemData.find(
        (item) => item._id === formData.item_id
      );
      if (selectedItem) {
        setFormData((prev) => ({
          ...prev,
          // item_thickness: editingIndex === null ? selectedItem?.thickness : prev.item_thickness,
          unit: selectedItem?.unit?.name || "",
          category: selectedItem?.category?.name || "",
          gst:
            editingIndex === null
              ? selectedItem?.gst_percentage || ""
              : prev.gst,
        }));
      }
    }
  }, [formData.item_id, itemData]);

  useEffect(() => {
    if (formData.item_id) {
      setTotalQty(stockData?.balance_stock || 0);
    } else {
      setTotalQty(0);
    }
  }, [formData.item_id, stockData]);

  const fetchData = () => {
    const bodyFormData = new URLSearchParams();
    bodyFormData.append("filter", JSON.stringify(filter));
    bodyFormData.append("firm_id", localStorage.getItem("PAY_USER_FIRM_ID"));
    dispatch(getProStock({ formData: bodyFormData }));
    setDisable(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const qty = parseFloat(formData.quantity) || parseFloat(formData.req_qty) || 0;
    const rate = parseFloat(formData.rate) || 0;
    const gst = parseFloat(formData.gst) || 0;

    if (qty > 0 && rate > 0) {
      const amount = qty * rate;
      const gst_amount = (amount * gst) / 100;
      const total_amount = amount + gst_amount;

      setFormData((prev) => ({
        ...prev,
        amount: amount.toFixed(2),
        gst_amount: gst_amount.toFixed(2),
        total_amount: total_amount.toFixed(2),
      }));
    } else if (qty === 0 || rate === 0) {
      setFormData((prev) => ({
        ...prev,
        amount: "",
        gst_amount: "",
        total_amount: "",
      }));
    }
  }, [formData.quantity, formData.req_qty, formData.rate, formData.gst]);

  useEffect(() => {
    if (!modal.isShown) {
      setFormData(initialFormState);
      setEditingIndex(null);
      setEditItemId(null);
      setErrors({});
    }
  }, [modal.isShown]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      // if (name === "quantity") {
      //   const qty = Number(value) || 0;

      //   if (qty > totalQty) {
      //     newErrors.quantity = `Issue quantity cannot be more than ${totalQty}`;
      //   } else if (qty <= 0) {
      //     newErrors.quantity = "Valid quantity is required";
      //   } else {
      //     delete newErrors.quantity;
      //   }
      // } else if (newErrors[name]) {
      //   delete newErrors[name];
      // }

      if (newErrors[name]) {
        delete newErrors[name];
      }

      return newErrors;
    });
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.item_id) newErrors.item_id = "Item is required";

    const qty = Number(formData.quantity) || 0;

    // if (qty <= 0) {
    //   newErrors.quantity = "Valid quantity is required";
    // } else if (qty > totalQty) {
    //   newErrors.quantity = `Issue quantity cannot be more than ${totalQty}`;
    // }

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = "Valid quantity is required";
    }

    if (!formData.project_id) newErrors.project_id = "Project is required";

    if (!formData.rate || parseFloat(formData.rate) <= 0)
      newErrors.rate = 'Valid rate is required';
    // if (!formData.heat_no) newErrors.heat_no = "Heat No. is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormAction = async (e, isAddMore = false) => {
    e.preventDefault();
    if (!validateForm()) return;
    setDisable(true);
    try {
      if (editItemId) {
        const payload = {
          id: data._id,
          item_detail_id: editItemId,
          items_details: {
            item_length: formData.item_length,
            item_width: formData.item_width,
            item_weight: formData.item_weight,
            item_radius: formData.item_radius,
            item_thickness: formData.item_thickness,
            assembly_weight: formData.assembly_weight,
            assembly_surface_area: formData.assembly_surface_area,
            quantity: formData.quantity,
            req_qty: formData.req_qty,
            rate: formData.rate,
            amount: formData.amount,
            gst: formData.gst,
            gst_amount: formData.gst_amount,
            total_amount: formData.total_amount,
            heat_no: formData.heat_no,
            plat_no: formData.plat_no,
            tc_no: formData.tc_no,
            remarks: formData.remarks,
            project_id: formData.project_id,
            is_returnable: Boolean(formData.is_returnable),
          },
        };
        const resp = await axios.put(
          `${V_URL}/prosafe/pro-update-iss-item`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("PAY_USER_TOKEN")}`,
            },
          }
        );
        if (resp.data.success) {
          toast.success(resp.data.message);
          dispatch(getViewIssItems({ formData: { id: data._id } }));
          if (isAddMore) {
            setFormData(initialFormState);
            setEditingIndex(null);
            setEditItemId(null);
            setErrors({});
          } else {
            modal.hide();
            setEditingIndex(null);
            setEditItemId(null);
          }
        }
      } else if (data?._id) {
        const payload = {
          id: data._id,
          items_details: JSON.stringify([formData]),
        };
        const resp = await axios.post(
          `${V_URL}/prosafe/pro-add-iss-item`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("PAY_USER_TOKEN")}`,
            },
          }
        );
        if (resp.data.success) {
          toast.success(resp.data.message);
          dispatch(getViewIssItems({ formData: { id: data._id } }));
          if (isAddMore) {
            setFormData(initialFormState);
            setEditingIndex(null);
            setEditItemId(null);
            setErrors({});
          } else {
            modal.hide();
          }
        } else {
          toast.error(resp.data.message);
        }
      } else {
        onSubmit(formData);
        if (isAddMore) {
          setFormData(initialFormState);
          setEditingIndex(null);
          setEditItemId(null);
          setErrors({});
        } else {
          modal.hide();
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setDisable(false);
    }
  };

  const isDisabled = editItemId || formData.isAuto;

  // useEffect(() => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     plat_no: "",
  //   }));
  // }, [formData.item_id]);

  return (
    <ModalWrapper
      title="Manual Items Details"
      isShown={modal.isShown}
      hide={modal.hide}
      footer={false}
      customSize={"modal-90w"}
    >
      <form>
        <div className="row">
          <div className="col-lg-3 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>
                Project <span className="login-danger">*</span>
              </label>
              <Dropdown
                placeholder="Select Project"
                filter
                value={formData.project_id}
                options={projectData?.map((e) => ({
                  label: `${e.name} ${e.project_no && `(${e.project_no})`}`,
                  value: e._id,
                }))}
                showClear={!!formData.project_id}
                onChange={(e) => handleDropdownChange("project_id", e.value)}
                filterBy="label"
                disabled={isDisabled}
                enforceFocus={false}
                className={`w-100 ${errors.project_id ? "p-invalid" : ""}`}
              />
              {errors.project_id && (
                <div className="error">{errors.project_id}</div>
              )}
            </div>
          </div>
          <div className="col-lg-3 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>
                Item Name <span className="login-danger">*</span>
              </label>
              <Dropdown
                placeholder="Select Item"
                filter
                value={formData.item_id}
                options={itemData.map((e) => ({
                  label: e?.name,
                  value: e?._id,
                }))}
                showClear={!!formData.item_id}
                onChange={(e) => handleDropdownChange("item_id", e.value)}
                filterBy="label"
                enforceFocus={false}
                appendTo="self"
                className={`w-100 ${errors.item_id ? "p-invalid" : ""}`}
                disabled={isDisabled}
              />
              {errors.item_id && (
                <small className="p-error">{errors.item_id}</small>
              )}
            </div>
          </div>
          {formData.item_id && (
            <>
              <div className="col-lg-3 col-md-6 col-12">
                <div className="input-block local-forms">
                  <label>Unit</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.unit || "-"}
                    disabled
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6 col-12">
                <div className="input-block local-forms">
                  <label>Category</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.category || "-"}
                    disabled
                  />
                </div>
              </div>
            </>
          )}

          {/* {formData.item_id && !editItemId && (
            <div className="col-lg-4 col-md-6 col-12">
              <div className="input-block local-forms">
                <label>Plate No.</label>

                <Dropdown
                  value={formData.plat_no}
                  options={filteredPlates.map((p) => ({
                    label: p.plat_no,
                    value: p.plat_no, 
                  }))}
                  onChange={(e) => handleDropdownChange("plat_no", e.value)}
                  placeholder="Select Plate No"
                  filter
                  showClear={!!formData.plat_no} 
                  className={`w-100`}
                  appendTo="self"
                />

                {errors.plat_no && (
                  <small className="p-error">{errors.plat_no}</small>
                )}
              </div>
            </div>
          )} */}

          <div className="col-lg-3 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>Item No</label>
              <input
                type="text"
                className="form-control"
                name="item_no"
                value={formData.item_no}
                onChange={handleChange}
              // disabled={isDisabled}
              />
            </div>
          </div>

          {/* Total Qty (Stock Balance) */}
          <div className="col-lg-3 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>Stock Qty</label>
              <input
                type="number"
                className="form-control"
                value={totalQty}
                disabled
              />
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>Requested Qty</label>
              <input
                type="text"
                className="form-control"
                name="req_qty"
                value={formData.req_qty}
                onChange={handleChange}
              // disabled={isDisabled}
              />
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>Avg Rate (₹)</label>
              <input
                type="text"
                className="form-control"
                name="avg_rate"
                value={formData.item_id ? (avgRate || "0.00") : ""}
                placeholder="0.00"
                disabled
              />
            </div>
          </div>

          {formData.item_id && (
            <div className="col-12 my-2">
              <div className="card border-info shadow-sm">
                <div className="card-header bg-light d-flex justify-content-between align-items-center py-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold text-primary" style={{ fontSize: "0.9rem" }}>
                      <i className="fa fa-history me-2"></i>
                      In-Time Receiving Rates & Cost Helper (Date-wise)
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {receivingRates && receivingRates.length > 0 && parseFloat(avgRate) > 0 && (
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-primary py-1 px-2"
                        style={{ fontSize: "0.75rem" }}
                        onClick={handleApplyAvgRate}
                      >
                        Apply Avg Rate (₹{avgRate})
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary py-0"
                      onClick={() => setShowRateHelp(!showRateHelp)}
                    >
                      {showRateHelp ? "Hide Helper" : "Show Helper"}
                    </button>
                  </div>
                </div>
                {showRateHelp && (
                  <div className="card-body p-2">
                    {loadingReceivingRates ? (
                      <div className="text-center py-2 text-muted" style={{ fontSize: "0.85rem" }}>
                        Loading receiving history...
                      </div>
                    ) : receivingRates && receivingRates.length > 0 ? (
                      <div className="table-responsive" style={{ maxHeight: "180px", overflowY: "auto" }}>
                        <table className="table table-sm table-bordered table-striped mb-0" style={{ fontSize: "0.82rem" }}>
                          <thead className="table-secondary sticky-top">
                            <tr>
                              <th>Rec. Date</th>
                              <th>Voucher / Bill No.</th>
                              <th>Party / Supplier</th>
                              <th className="text-end">Rec. Qty</th>
                              <th className="text-end">Unit Rate (₹)</th>
                              <th className="text-end">GST (%)</th>
                              <th className="text-end">Total Amount (₹)</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {receivingRates.map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.trans_date ? moment(row.trans_date).format("YYYY-MM-DD") : "-"}</td>
                                <td>{row.voucher_no || row.bill_no || "-"}</td>
                                <td>{row.party_name || "-"}</td>
                                <td className="text-end">{row.in_qty || 0}</td>
                                <td className="text-end fw-bold text-success">
                                  {row.rate ? Number(row.rate).toFixed(2) : "0.00"}
                                </td>
                                <td className="text-end">{row.gst || 0}%</td>
                                <td className="text-end">
                                  {row.total_amount ? Number(row.total_amount).toFixed(2) : "0.00"}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-primary py-0 px-2"
                                    style={{ fontSize: "0.75rem" }}
                                    onClick={() => handleApplyReceivingRate(row)}
                                  >
                                    Apply Rate
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-2 text-muted" style={{ fontSize: "0.82rem" }}>
                        No past receiving history found for this item.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {[
            // { name: 'item_no', label: 'Item No', type: 'text', disabled: isDisabled },
            {
              name: "quantity",
              label: "Issue Qty",
              required: true,
              type: "number",
              disabled: editItemId,
            },
            // { name: "item_length", label: "Length", type: "number" },
            // { name: "item_width", label: "Width", type: "number" },
            // { name: "item_weight", label: "Weight", type: "number" },
            // { name: "item_radius", label: "Radius", type: "number" },
            // { name: "item_thickness", label: "Thickness", type: "text" },
            // {
            //   name: "assembly_weight",
            //   label: "Assembly Weight",
            //   type: "number",
            // },
            // {
            //   name: "assembly_surface_area",
            //   label: "Surface Area",
            //   type: "number",
            // },
            { name: 'rate', label: 'Rate(₹)', required: true, type: 'number' },
            { name: 'amount', label: 'Amount(₹)', type: 'number', disabled: true },
            { name: 'gst', label: 'GST(%)', type: 'number' },
            { name: 'gst_amount', label: 'GST Amount(₹)', type: 'number', disabled: true },
            { name: 'total_amount', label: 'Total Amount(₹)', type: 'number', disabled: true },
            {
              name: "heat_no",
              label: "Heat No.",
              required: false,
              type: "text",
            },
            { name: "plat_no", label: "Plat No.", type: "text" },
            { name: "tc_no", label: "TC No.", type: "text" },
            { name: "remarks", label: "Remarks", type: "text" },
          ].map(({ name, label, required, type, disabled }) => (
            <div key={name} className="col-lg-3 col-md-6 col-12">
              <div className="input-block local-forms">
                <label>
                  {label} {required && <span className="login-danger">*</span>}
                </label>
                <input
                  type={type || "text"}
                  className={`form-control ${errors[name] ? "is-invalid" : ""}`}
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  disabled={disabled || false}
                />
                {errors[name] && (
                  <div className="invalid-feedback">{errors[name]}</div>
                )}
              </div>
            </div>
          ))}

          <div className="col-lg-3 col-md-6 col-12">
            <div className="input-block local-forms">
              <label>Returnable/Not Returnable</label>
              <Dropdown
                value={formData.is_returnable === true ? true : false}
                options={[
                  { label: "Not Returnable", value: false },
                  { label: "Returnable", value: true },
                ]}
                onChange={(e) => handleDropdownChange("is_returnable", e.value)}
                placeholder="Select Returnable"
                className="w-100"
              />
            </div>
          </div>
        </div>
        <div className="col-12 text-end">
          <div className="text-end">
            <button
              type="button"
              className="btn btn-primary"
              disabled={disable}
              onClick={(e) => handleFormAction(e, false)}
            >
              {editingIndex !== null ? "Update" : "Submit"}
            </button>
            {editingIndex === null && (
              <button
                type="button"
                className="btn btn-outline-primary m-2"
                onClick={(e) => handleFormAction(e, true)}
              >
                Add More
              </button>
            )}
          </div>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default ManualIssueModel;
